import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCreateTestChat } from '@/hooks/useCreateTestChat';
import { useToast } from '@/hooks/use-toast';
import { getErrorMessage } from '@/lib/error-handler';

const schema = z.object({
  senderName: z.string().min(1, 'Informe o nome do remetente').max(100, 'Máximo 100 caracteres'),
  phoneNumber: z.string().min(1, 'Informe o número de WhatsApp').max(50, 'Máximo 50 caracteres'),
});

type FormData = z.infer<typeof schema>;

interface NewTestChatModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (threadId: string) => void;
}

export function NewTestChatModal({ open, onClose, onSuccess }: NewTestChatModalProps) {
  const { mutate, isPending, reset } = useCreateTestChat();
  const { error: toastError } = useToast();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset: resetForm,
  } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  function handleClose() {
    resetForm();
    reset();
    onClose();
  }

  function onSubmit(data: FormData) {
    mutate(
      { phoneNumber: data.phoneNumber, senderName: data.senderName },
      {
        onSuccess: (result) => {
          resetForm();
          reset();
          onSuccess(result.threadId);
        },
        onError: (err) => {
          toastError(getErrorMessage(err, 'Erro ao criar chat de teste'));
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) handleClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Novo Chat de Teste</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="senderName">Nome do Remetente</Label>
            <Input
              id="senderName"
              placeholder="Ex: João Silva"
              {...register('senderName')}
              disabled={isPending}
            />
            {errors.senderName && (
              <p className="text-sm text-destructive">{errors.senderName.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="phoneNumber">Número / ID</Label>
            <Input
              id="phoneNumber"
              placeholder="Ex: 5511999998888 ou teste-1"
              {...register('phoneNumber')}
              disabled={isPending}
            />
            {errors.phoneNumber && (
              <p className="text-sm text-destructive">{errors.phoneNumber.message}</p>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} disabled={isPending}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Criar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
